/**
 * Parse a raw keylogger data blob into categorized events.
 *
 * A keylogger upload (`data`) mixes several kinds of activity on one stream:
 *   - window title / focus changes:  `--- [Windows: <title>] ---`
 *   - clipboard snapshots / config:   `[Clipboard: <json>]`
 *   - raw keystrokes / typed text:    any other non-empty line
 *
 * The previous implementation rendered the whole blob as one monolithic
 * line, which is why the view stopped looking structured. This module
 * classifies each line so the UI can render each kind appropriately.
 * Consecutive keystroke lines are merged into a single event.
 */

export type KeylogEventType = "keystroke" | "window" | "clipboard";

export interface KeylogKeystroke {
  type: "keystroke";
  text: string;
}

export interface KeylogWindow {
  type: "window";
  title: string;
}

export interface KeylogClipboard {
  type: "clipboard";
  /** The original `<json>` payload verbatim (used for copy). */
  raw: string;
}

export type KeylogEvent = KeylogKeystroke | KeylogWindow | KeylogClipboard;

const WINDOW_RE = /^\s*---\s*\[Windows:\s*(.*?)\]\s*---\s*$/;
const CLIPBOARD_RE = /^\s*\[Clipboard:\s*(.*?)\]\s*$/;

/** Is a line a `--- [Windows: ...] ---` window marker? */
export function isWindowLine(line: string): boolean {
  return WINDOW_RE.test(line);
}

/** Is a line a `[Clipboard: ...]` clipboard marker? */
export function isClipboardLine(line: string): boolean {
  return CLIPBOARD_RE.test(line);
}

/** Extract the window title from a `--- [Windows: <title>] ---` line. */
export function extractWindowTitle(line: string): string {
  const m = WINDOW_RE.exec(line);
  return m ? m[1].trim() : "";
}

/** Extract the inner `<json>` text from a `[Clipboard: <json>]` line. */
export function extractClipboardRaw(line: string): string {
  const m = CLIPBOARD_RE.exec(line);
  return m ? m[1].trim() : "";
}

/**
 * Parse a raw keylogger `data` blob into an ordered list of events.
 * Blank lines act as separators (skipped). Consecutive keystroke lines
 * are merged into one event so they render as a single text block.
 */
export function parseKeylogData(raw: string | null | undefined): KeylogEvent[] {
  if (!raw) return [];

  const lines = raw.replace(/\r/g, "").split("\n");
  const events: KeylogEvent[] = [];
  let pendingText: string[] = [];

  const flush = () => {
    if (pendingText.length > 0) {
      events.push({ type: "keystroke", text: pendingText.join("\n") });
      pendingText = [];
    }
  };

  for (const line of lines) {
    if (line.trim() === "") continue;

    if (isWindowLine(line)) {
      flush();
      events.push({ type: "window", title: extractWindowTitle(line) });
      continue;
    }

    if (isClipboardLine(line)) {
      flush();
      events.push({ type: "clipboard", raw: extractClipboardRaw(line) });
      continue;
    }

    pendingText.push(line);
  }
  flush();

  return events;
}
